import DOMPurify from 'dompurify';
import { DEFAULT_PAPER, SUPPORTED_PAPERS } from './paperSizes';

var userAgent = navigator.userAgent.toLowerCase();
var isElectron = (userAgent.indexOf(' electron/') > -1)

const defaultPaper = DEFAULT_PAPER;

// Only allowed paper names get interpolated into the printed CSS.
const VALID_PAPER_NAMES = new Set(Object.values(SUPPORTED_PAPERS).map(p => p.name));

// The printable sheet is a constrained SVG/HTML tree (MUI-rendered canvas +
// creature images/labels, see SheetCanvas.js/MiniView.js). Restrict to
// exactly what that tree emits.
const SANITIZE_CONFIG = {
    ALLOWED_TAGS: [
        'div', 'svg', 'g', 'image', 'text', 'tspan', 'rect', 'path', 'line', 'use',
        'img', 'filter', 'feGaussianBlur'
    ],
    ALLOWED_ATTR: [
        'class', 'id', 'style', 'viewBox', 'width', 'height', 'x', 'y', 'x1', 'y1', 'x2', 'y2',
        'rx', 'd', 'transform', 'src', 'href', 'xlink:href', 'alt', 'preserveAspectRatio',
        'clip-path', 'fill', 'stroke', 'stroke-width', 'stroke-dasharray', 'fill-opacity',
        'text-anchor', 'font-family', 'font-size', 'font-style', 'stdDeviation'
    ],
    // Deliberately NOT overriding ALLOWED_URI_REGEXP: DOMPurify applies it as a
    // general "does this attribute value look like a disallowed-protocol URI"
    // check across many attributes, not just href/src. Its default pattern has a
    // permissive fallback (values not starting with a lowercase letter, e.g. SVG
    // numeric attrs like `viewBox="0 0 10 10"`) that a narrower custom regex loses,
    // silently stripping unrelated layout attributes. Data-URI creature images are
    // allowed instead via ADD_DATA_URI_TAGS; http(s) and javascript:-blocking are
    // already handled by the default regex.
    ADD_DATA_URI_TAGS: ['image'],
    FORBID_TAGS: ['script', 'style'],
};

// ADD_DATA_URI_TAGS permits any data: MIME type on <image>, including
// image/svg+xml (which can carry an embedded <script>). Browsers don't
// execute scripts in SVGs loaded this way, but restrict to bitmap formats
// as defense-in-depth rather than relying on that.
const SAFE_DATA_IMAGE_MIME = /^data:image\/(?:png|jpe?g|webp|gif);base64,/i;
let hookInstalled = false;
function ensureImageMimeHook() {
    if (hookInstalled) return;
    DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
        if (data.attrName === 'href' && node.tagName && node.tagName.toLowerCase() === 'image') {
            if (/^data:/i.test(data.attrValue) && !SAFE_DATA_IMAGE_MIME.test(data.attrValue)) {
                data.keepAttr = false;
            }
        }
    });
    hookInstalled = true;
}

function sanitizePrintable(element) {
    ensureImageMimeHook();
    return DOMPurify.sanitize(element.outerHTML, SANITIZE_CONFIG);
}

// Matches the cap enforced on the Electron IPC path (public/electron.js) so
// an oversized packed sheet fails the same way in both runtimes, instead of
// only being guarded when running inside Electron.
const MAX_PRINT_HTML_BYTES = 10 * 1024 * 1024;

export function print(element, paperSize) {
    return new Promise((resolve, reject) => {
        const paperName = paperSize && VALID_PAPER_NAMES.has(paperSize.name) ? paperSize.name : defaultPaper.name;
        const safeHtml = sanitizePrintable(element);

        const content = `<html><head>
    <style media="print">
    @page {
        size: ${paperName} landscape;
        margin: 0;
        padding: 0;
    };
    .noprint {
        display: none !important;
    }
    /* Chromium's print path ignores display:none on inline SVG geometry, so the
       hover outline rect (class "noprint") leaks into the printout. Neutralize
       its paint too — stroke/fill CSS overrides the SVG presentation attributes
       and is honored during printing. */
    .print-hide-stroke {
        stroke: none !important;
        fill: none !important;
    }
    </style>
    </head>
    <body>
    ${safeHtml}
    <img class="statcounter" src="https://c.statcounter.com/12835131/0/593e06d7/1/" alt="Web Analytics" referrerpolicy="no-referrer-when-downgrade">
    </body>
    </html>`;

        if (new Blob([content]).size > MAX_PRINT_HTML_BYTES) {
            reject(new Error("Print payload too large"));
            return;
        }

        if (isElectron) {
            window.electronAPI.print(content).then(resolve).catch(reject);
            return;
        }

        // Browser: render into a hidden iframe (no visible tab) and trigger
        // the print dialog once images have finished loading (the iframe's
        // onload waits on external resources, not just DOM parsing).
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        iframe.style.visibility = 'hidden';
        document.body.appendChild(iframe);

        const cleanup = () => {
            if (iframe.parentNode) iframe.parentNode.removeChild(iframe);
        };

        try {
            const frameDoc = iframe.contentWindow.document;
            frameDoc.open();
            frameDoc.write(content);
            frameDoc.close();
            iframe.onload = () => {
                try {
                    iframe.contentWindow.focus();
                    iframe.contentWindow.print();
                } catch (e) {
                    cleanup();
                    reject(e);
                    return;
                }
                iframe.contentWindow.onafterprint = cleanup;
                setTimeout(cleanup, 60000); // fallback if onafterprint never fires
            };
            resolve();
        } catch (e) {
            cleanup();
            reject(e);
        }
    });
}
